import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useState, useMemo } from "react";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  GraduationCap,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  UserCircle,
  ClipboardList,
  BarChart3,
  FileBarChart2,
  ChevronRight
} from "lucide-react";

import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import Notifications from "./Notifications";

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reportesOpen, setReportesOpen] = useState(
    location.pathname.startsWith('/admin/reports')
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = useMemo(() => {
    const baseItems = [
      { path: "/dashboard", label: "Vista General", icon: LayoutDashboard },
      { path: "/courses", label: "Mis Cursos", icon: BookOpen },
    ];

    const adminItems = [
      { path: "/admin/users", label: "Usuarios", icon: Users },
      { path: "/admin/courses", label: "Gestión Cursos", icon: GraduationCap },
      { path: "/admin/enrollments", label: "Matrículas", icon: ClipboardList }
    ];

    const profesorItems = [
      { path: "/admin/courses", label: "Gestión Cursos", icon: GraduationCap },
      { path: "/admin/enrollments", label: "Matrículas", icon: ClipboardList }
    ];

    const studentExtras = [
      { path: "/grades", label: "Mis Calificaciones", icon: BarChart3 }
    ];

    if (user?.role === "ADMIN") {
      return [...baseItems, ...adminItems, { path: "/admin/reports", label: "Reportes", icon: FileBarChart2, isGroup: true }];
    } else if (user?.role === "PROFESOR") {
      return [...baseItems, ...profesorItems];
    }
    
    return [...baseItems, ...studentExtras];
  }, [user?.role]);


  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 border-r transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ borderColor: 'hsl(160 30% 25%)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b" style={{ borderColor: 'hsl(160 30% 25%)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--primary))' }}>
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-lg font-heading">LearnIA</h1>
              <p className="text-xs text-emerald-200">Sistema LMS</p>
            </div>
            <button
              className="lg:hidden ml-auto text-emerald-200 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;

              if (item.isGroup) {
                const isSubActive = location.pathname.startsWith('/admin/reports');
                return (
                  <div key={item.path}>
                    <button
                      className={`sidebar-item w-full ${isSubActive ? "active" : ""}`}
                      onClick={() => setReportesOpen(o => !o)}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronRight className={`w-4 h-4 transition-transform ${reportesOpen ? "rotate-90" : ""}`} />
                    </button>
                    {reportesOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        <Link
                          to="/admin/reports/usuarios"
                          className={`sidebar-item text-sm ${location.pathname === '/admin/reports/usuarios' ? "active" : ""}`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <FileBarChart2 className="w-4 h-4" />
                          <span>Reporte de Usuarios</span>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                  className={`sidebar-item ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info at bottom */}
          <div className="p-4 border-t" style={{ borderColor: 'hsl(160 30% 25%)' }}>
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'hsl(var(--primary))' }}>
                <UserCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-emerald-200">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="topbar-glass sticky top-0 z-30 h-16 flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <Notifications />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <UserCircle className="w-5 h-5 text-primary" />
                  </div>
                  <span className="hidden md:block text-sm font-medium">{user?.full_name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-btn">
                  <UserCircle className="w-4 h-4 mr-2" />
                  Mi Perfil
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="settings-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Configuración
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
